/**
 * PrivacyToggle - global "screen-share safe" switch.
 *
 * Flips Privacy mode on/off. When on, sensitive deal/contact values across the
 * app are blurred (see client/src/lib/privacy.ts + the `.privacy-active` rule in
 * index.css) so the operator can navigate and share their screen safely.
 * Mirrors the theme/density toggle pattern.
 */
import { Eye, EyeOff } from 'lucide-react';
import { usePrivacy } from '../../lib/privacy.js';
import { Tooltip } from './tooltip.js';
import { cn } from '../../lib/utils.js';

interface PrivacyToggleProps {
  className?: string;
  /** Show an inline text label next to the icon. */
  showLabel?: boolean;
}

export default function PrivacyToggle({ className, showLabel }: PrivacyToggleProps) {
  const { enabled, toggle } = usePrivacy();
  const Icon = enabled ? EyeOff : Eye;
  const stateLabel = enabled ? 'On' : 'Off';
  const nextLabel = enabled ? 'Show sensitive data' : 'Blur sensitive data for screen-sharing';

  return (
    <Tooltip
      content={
        <span>
          <span className="opacity-70">Privacy: </span>
          <span className="font-semibold">{stateLabel}</span>
          <br />
          <span className="opacity-70">{nextLabel}</span>
        </span>
      }
      variant="rich"
      side="right"
    >
      <button
        type="button"
        onClick={toggle}
        aria-label={`Privacy mode ${stateLabel}. ${nextLabel}.`}
        aria-pressed={enabled}
        data-testid="button-privacy-toggle"
        className={cn(
          'inline-flex items-center justify-center gap-1.5 rounded-md transition-colors',
          enabled
            ? 'text-primary hover:bg-primary/10'
            : 'text-ink-muted hover:text-ink hover:bg-surface-sunken',
          showLabel ? 'px-2 py-1.5 text-xs' : 'h-9 w-9',
          className,
        )}
      >
        <Icon size={16} />
        {showLabel && <span>{enabled ? 'Privacy on' : 'Privacy'}</span>}
      </button>
    </Tooltip>
  );
}
